import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  articlePath,
  compareArticles,
  groupArticles,
  searchArticles,
  type HelpAudience,
} from '../help/articles';
import { MarkdownView } from '../help/markdown-view';
import { articlesFor, getArticle } from '../help/registry';
import { cn } from '../lib/utils';
import { Button } from './button';
import { PageTitle, containerClass } from './app-shell';
import { Icon } from './icons';
import { Input } from './input';

export interface HelpCenterProps {
  /** Whose articles to list: admin app → `admin`, seller portal → `seller`, buyer portal → `buyer`. */
  audience: HelpAudience;
  /** Offers a "Contact support" button when the app has a support form (admin). */
  onContactSupport?: () => void;
}

const INTRO: Record<HelpAudience, string> = {
  admin: 'Step-by-step guides for running a data room.',
  seller: 'Step-by-step guides for sharing your documents and following the deal.',
  buyer: 'Step-by-step guides for reviewing the data room.',
};

/**
 * The help center page, mounted at `/help/*` in each app: the audience's
 * articles grouped by category with a search box, or — at `/help/<audience>/<slug>`
 * — one article, with related articles from the same category. A `#anchor` in
 * the URL scrolls to that `##` section, which is how a HelpTip deep-links.
 */
export function HelpCenter({ audience, onContactSupport }: HelpCenterProps) {
  const splat = (useParams()['*'] ?? '').replace(/^\/+|\/+$/g, '');
  const { hash } = useLocation();
  const article = splat ? getArticle(splat) : undefined;
  const shown = article && article.audience === audience ? article : undefined;

  // Land at the top of a new article, or on its #section.
  const articleId = shown?.id;
  useEffect(() => {
    const anchor = decodeURIComponent(hash.replace(/^#/, ''));
    const target = anchor ? document.getElementById(anchor) : null;
    if (target) target.scrollIntoView?.({ block: 'start' });
    else window.scrollTo?.({ top: 0 });
  }, [articleId, hash]);

  return (
    <div className={cn(containerClass, 'py-6 pb-16')}>
      {shown ? (
        <ArticleView article={shown} audience={audience} />
      ) : (
        <HelpIndex audience={audience} missing={splat !== ''} />
      )}
      {onContactSupport ? (
        <section
          aria-label="Contact support"
          className="mt-12 flex max-w-[68ch] flex-wrap items-center justify-between gap-3 rounded-lg border bg-surface p-4"
        >
          <p className="text-sm text-ink-2">Can&apos;t find what you need? Ask us directly.</p>
          <Button variant="outline" size="sm" onClick={onContactSupport}>
            Contact support
          </Button>
        </section>
      ) : null}
    </div>
  );
}

function HelpIndex({ audience, missing }: { audience: HelpAudience; missing: boolean }) {
  const [query, setQuery] = useState('');
  const articles = useMemo(() => articlesFor(audience), [audience]);
  const groups = useMemo(() => groupArticles(articles), [articles]);
  const results = query.trim() ? searchArticles(articles, query) : null;

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <PageTitle>Help</PageTitle>
        <p className="text-sm text-ink-2">{INTRO[audience]}</p>
      </div>
      {missing ? (
        <p role="status" className="rounded-md border bg-surface-2 px-3 py-2 text-sm">
          We couldn&apos;t find that article. Pick one below or search.
        </p>
      ) : null}
      <div className="max-w-xl">
        <label htmlFor="help-search" className="sr-only">
          Search help
        </label>
        <Input
          id="help-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search help…"
          autoComplete="off"
        />
      </div>

      {results ? (
        results.length > 0 ? (
          <section aria-label="Search results">
            <p className="mb-2 text-sm text-ink-2" role="status">
              {results.length} {results.length === 1 ? 'article' : 'articles'} found
            </p>
            <ArticleList articles={results} showCategory />
          </section>
        ) : (
          <p role="status" className="text-sm text-ink-2">
            No articles match &ldquo;{query.trim()}&rdquo;. Try fewer or different words.
          </p>
        )
      ) : groups.length > 0 ? (
        <div className="space-y-8">
          {groups.map((g) => (
            <section key={g.category} aria-labelledby={`help-cat-${g.category}`}>
              <h2
                id={`help-cat-${g.category}`}
                className="mb-2 font-serif text-xl font-semibold leading-7"
              >
                {g.category}
              </h2>
              <ArticleList articles={g.articles} />
            </section>
          ))}
        </div>
      ) : (
        <p className="text-sm text-ink-2">No help articles yet.</p>
      )}
    </div>
  );
}

function ArticleList({
  articles,
  showCategory = false,
}: {
  articles: ReturnType<typeof articlesFor>;
  showCategory?: boolean;
}) {
  return (
    <ul className="divide-y rounded-lg border bg-surface">
      {articles.map((a) => (
        <li key={a.id}>
          <Link
            to={articlePath(a.id)}
            className="flex min-h-tap items-center justify-between gap-3 px-4 py-3 hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
          >
            <span className="min-w-0">
              <span className="block font-medium text-ink">{a.title}</span>
              <span className="mt-0.5 block text-sm text-ink-2">{a.summary}</span>
              {showCategory ? (
                <span className="mt-1 block text-xs text-ink-3">{a.category}</span>
              ) : null}
            </span>
            <Icon name="chevronRight" className="shrink-0 text-ink-3" />
          </Link>
        </li>
      ))}
    </ul>
  );
}

function ArticleView({
  article,
  audience,
}: {
  article: NonNullable<ReturnType<typeof getArticle>>;
  audience: HelpAudience;
}) {
  const related = articlesFor(audience)
    .filter((a) => a.category === article.category && a.id !== article.id)
    .sort(compareArticles)
    .slice(0, 5);

  return (
    <article className="space-y-6">
      <div className="space-y-2">
        <Link
          to="/help"
          className="inline-flex w-fit items-center gap-1.5 text-[13px] text-ink-2 hover:underline"
        >
          <Icon name="arrowLeft" size={14} />
          All help articles
        </Link>
        <p className="text-xs font-medium uppercase tracking-wide text-ink-3">{article.category}</p>
        <PageTitle>{article.title}</PageTitle>
        <p className="max-w-[68ch] text-base text-ink-2">{article.summary}</p>
      </div>
      <MarkdownView blocks={article.blocks} />
      {related.length > 0 ? (
        <section aria-labelledby="help-related" className="max-w-[68ch] border-t pt-6">
          <h2 id="help-related" className="mb-2 font-serif text-xl font-semibold leading-7">
            Related articles
          </h2>
          <ArticleList articles={related} />
        </section>
      ) : null}
    </article>
  );
}
